(() => {
  const controllerKey = 'mhadlyCartTermsController';

  if (window[controllerKey]) {
    window[controllerKey].sync();
    return;
  }

  const scopeSelector = 'cart-items-component';
  const generalSelector = 'input[name="attributes[terms_conditions_accepted]"]';
  const specificSelector = 'input[name="attributes[specific_terms_conditions_accepted]"]';
  const checkboxSelector = '.cart-terms__checkbox';
  const ctasSelector = '.cart__ctas[data-terms-toggle]';
  const errorSelector = '[data-cart-terms-error]';
  const retrySelector = '[data-cart-terms-retry]';
  const root = window.Shopify?.routes?.root || '/';
  const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
  const updateUrl = `${normalizedRoot}cart/update.js`;

  const state = {
    confirmed: null,
    desired: null,
    saving: false,
    error: false,
    requestId: 0,
    abortController: null,
  };

  const scopes = () =>
    Array.from(document.querySelectorAll(scopeSelector)).filter(
      (scope) => scope.querySelector(generalSelector) && scope.querySelector(specificSelector)
    );

  const readScope = (scope) => ({
    general: Boolean(scope.querySelector(generalSelector)?.checked),
    specific: Boolean(scope.querySelector(specificSelector)?.checked),
  });

  const sameState = (left, right) =>
    Boolean(left) &&
    Boolean(right) &&
    left.general === right.general &&
    left.specific === right.specific;

  const applyInputs = (values) => {
    scopes().forEach((scope) => {
      const general = scope.querySelector(generalSelector);
      const specific = scope.querySelector(specificSelector);

      if (general) general.checked = values.general;
      if (specific) specific.checked = values.specific;
    });
  };

  const setInputsDisabled = (disabled) => {
    document.querySelectorAll(checkboxSelector).forEach((checkbox) => {
      checkbox.disabled = disabled;
      checkbox.setAttribute('aria-busy', String(disabled));
    });
  };

  const showError = (visible) => {
    document.querySelectorAll(errorSelector).forEach((message) => {
      message.hidden = !visible;
    });
  };

  const syncCtas = () => {
    scopes().forEach((scope) => {
      const values = readScope(scope);
      const ctas = scope.querySelector(ctasSelector);
      if (!ctas) return;

      const isCartEmpty = ctas.dataset.cartEmpty === 'true';
      const isConfirmed = sameState(values, state.confirmed);
      const isEnabled =
        values.general &&
        values.specific &&
        isConfirmed &&
        !isCartEmpty &&
        !state.saving &&
        !state.error;

      ctas.classList.toggle('cart__ctas--terms-disabled', !isEnabled);
      ctas.setAttribute('aria-busy', String(state.saving));

      ctas.querySelectorAll('.cart__checkout-button, [name="checkout"]').forEach((button) => {
        button.disabled = !isEnabled;
        button.setAttribute('aria-disabled', String(!isEnabled));
      });

      ctas.querySelectorAll('.additional-checkout-buttons').forEach((wrapper) => {
        wrapper.toggleAttribute('inert', !isEnabled);
        wrapper.setAttribute('aria-disabled', String(!isEnabled));
      });

      ctas
        .querySelectorAll('shopify-accelerated-checkout-cart, .cart__dynamic-checkout-buttons')
        .forEach((checkout) => {
          checkout.style.pointerEvents = isEnabled ? '' : 'none';
          checkout.style.opacity = isEnabled ? '' : '0.5';
        });
    });
  };

  const syncFromDom = () => {
    const firstScope = scopes()[0];
    if (!firstScope) return;

    if (!state.saving && !state.error) {
      const renderedState = readScope(firstScope);
      state.confirmed = renderedState;
      state.desired = renderedState;
      applyInputs(renderedState);
    }

    syncCtas();
  };

  const persist = async (values) => {
    state.abortController?.abort();
    state.abortController = new AbortController();
    state.requestId += 1;

    const requestId = state.requestId;
    state.desired = values;
    state.saving = true;
    state.error = false;

    applyInputs(values);
    showError(false);
    setInputsDisabled(true);
    syncCtas();

    try {
      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            terms_conditions_accepted: values.general ? 'Yes' : '',
            specific_terms_conditions_accepted: values.specific ? 'Yes' : '',
          },
        }),
        signal: state.abortController.signal,
      });

      if (!response.ok) {
        throw new Error('Unable to update cart terms attributes');
      }

      const cart = await response.json();
      const persistedState = {
        general: cart.attributes?.terms_conditions_accepted === 'Yes',
        specific: cart.attributes?.specific_terms_conditions_accepted === 'Yes',
      };

      if (!sameState(persistedState, values)) {
        throw new Error('Cart terms attributes do not match the requested state');
      }

      if (requestId !== state.requestId) return;

      state.confirmed = persistedState;
      state.desired = persistedState;
      state.saving = false;
      state.error = false;

      applyInputs(persistedState);
      setInputsDisabled(false);
      showError(false);
      syncCtas();
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== state.requestId) return;

      state.saving = false;
      state.error = true;
      setInputsDisabled(false);
      showError(true);
      syncCtas();
    }
  };

  const handleChange = (event) => {
    if (!event.target.matches(checkboxSelector)) return;

    const scope = event.target.closest(scopeSelector);
    if (!scope) return;

    const values = readScope(scope);
    applyInputs(values);
    persist(values);
  };

  const handleClick = (event) => {
    const retry = event.target.closest(retrySelector);
    if (!retry || !state.desired || state.saving) return;

    persist(state.desired);
  };

  const handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

    const accepted =
      Boolean(state.confirmed?.general) &&
      Boolean(state.confirmed?.specific) &&
      !state.saving &&
      !state.error;

    if (accepted) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    syncCtas();
  };

  let animationFrame = 0;
  const scheduleSync = () => {
    if (animationFrame) return;

    animationFrame = requestAnimationFrame(() => {
      animationFrame = 0;
      syncFromDom();
    });
  };

  document.addEventListener('change', handleChange);
  document.addEventListener('click', handleClick);
  document.addEventListener('submit', handleSubmit, true);
  document.addEventListener('shopify:section:load', scheduleSync);
  window.addEventListener('pageshow', scheduleSync);

  new MutationObserver(scheduleSync).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window[controllerKey] = {
    sync: scheduleSync,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
  } else {
    scheduleSync();
  }
})();
